/**
 * Attendee API Client
 * ===================
 *
 * This client is different than most of our other subclients. Instead of authenticating Splash Users,
 * it hits endpoints authenticated by Splash Attendees!
 */
import pick from 'lodash/pick';
import merge from 'lodash/merge';

const PAGINATION_KEYS = ['page', 'limit'];

export default class AttendeeApiClient {
    /**
     * Constructor.
     *
     * @param {string} guestAccessHash
     */
    constructor(apiClient, guestAccessHash = false) {
        this.setGuestAccessHash(guestAccessHash);
        this.apiClient = apiClient;
    }

    /**
     * Requests an access token using the attendee's magic link and the clients that are specific to
     * the attendee scoped endpoints.
     *
     * @param {string} clientId
     * @param {string} clientSecret
     */
    fetchAccessToken(clientId, clientSecret) {
        return this.apiClient
            .post('/oauth/v2/token', {
                client_id: clientId,
                client_secret: clientSecret,
                grant_type: 'client_credentials',
                scope: 'attendee',
                meta: this.guestAccessHash,
            })
            .then((response) => {
                if (response && response.access_token) {
                    this.setAccessToken(response.access_token);
                }

                return response;
            })
            .catch((err) => {
                throw err;
            });
    }

    /**
     * RSVPs to the given session. No additional post body is necessary because the magic link will
     * identify which group contact is RSVPing.
     *
     * @param {integer} eventId
     * @param {integer} sessionId
     * @returns {Promise}
     */
    rsvpToSession(eventId, sessionId) {
        return this.apiClient.post(`/event/${eventId}/session/${sessionId}/attendee/rsvp`);
    }

    /**
     * Removes RSVP from the given session. No additional post body is necessary because the magic link will
     * identify which group contact RSVP to remove..
     *
     * @param {integer} eventId
     * @param {integer} sessionId
     * @returns {Promise}
     */
    removeRsvpFromSession(eventId, sessionId) {
        return this.apiClient.delete(`/event/${eventId}/session/${sessionId}/attendee/unrsvp`);
    }

    /**
     * Retrieves the sessions that the logged in attendee has RSVPed to.
     *
     * @param {integer} eventId
     * @returns {Promise}
     */
    fetchAttendeeSessions(eventId, pagination = { page: 1, limit: 10 }, params = {}) {
        const paginationParams = pick(pagination, PAGINATION_KEYS);
        const requestParams = merge(params, paginationParams);
        return this.apiClient.get(`/event/${eventId}/attendee/sessions`, requestParams);
    }

    /**
     * retrieve useful information about the attendee, particularly their event attendee status
     *
     * @param {integer} eventId
     * @returns {*}
     */
    fetchAttendeeProfile(eventId) {
        return this.apiClient.get('/attendee/me', {
            event_id: eventId,
        });
    }

    /**
     * Sends a GET request to download a calendar ICS file for the time of the session.
     */
    getSessionCalendarDownload(eventId, sessionId) {
        window.open(
            `${this.apiClient.baseUrl}/public/events/${eventId}/sessions/${sessionId}/calendar`,
            'SessionCalendarDownload',
        );
    }

    /**
     * Sends a GET request to download a calendar ICS file for all sessions that I am
     * attending.
     */
    getAgendaCalendarDownload(eventId) {
        window.open(
            `${this.apiClient.baseUrl}/events/${eventId}/attendee/sessions/calendar?access_token=${this.apiClient.accessToken}`,
            'AgendaCalendarDownload',
        );
    }

    /**
     * The guest access hash is a character string generated by the Splash API that identifies the
     * current attendee as a specific GroupContact in the backend system. We use this magic
     * link to redeem an API access token to request data.
     *
     * @param {string} guestAccessHash
     */
    setGuestAccessHash(guestAccessHash) {
        this.guestAccessHash = guestAccessHash;
    }

    /**
     * When a token is retrieved by exchanging a magic link, we need to set the access token on
     * the SplashApiClient so that future requests use that token.
     *
     * @param {string} accessToken
     */
    setAccessToken(accessToken) {
        this.apiClient.accessToken = accessToken;
    }

    /**
     * Determines whether or not we already have a populated attendee access token.
     *
     * @returns {bool}
     */
    hasAccessToken() {
        return !!this.apiClient.accessToken;
    }
}
